import React from "react";
import { Menu, Phone, X } from "lucide-react";
import { Button, ButtonLink } from "../../../components/ui/button";
import { LinkAnchor } from "../../../components/ui/link";
import { cn } from "../../../lib/utils";
import {
  CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
  CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
} from "../layoutRadius";
import {
  type ContractorSectionLink,
  publicSiteShowsFullQuoteForm,
} from "../sections";
import type { ContractorSitePageProps } from "../types";

type SiteNavbarProps = Pick<ContractorSitePageProps, "business"> & {
  sectionLinks: ContractorSectionLink[];
};

export const SiteNavbar: React.FC<SiteNavbarProps> = ({
  business,
  sectionLinks,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const showQuoteForm = publicSiteShowsFullQuoteForm(business);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    e.preventDefault();
    setIsOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${scrolled ? "bg-card/95 shadow-sm backdrop-blur-md" : "bg-background/90 backdrop-blur-sm"}`}
    >
      <div className="mx-auto h-16 max-w-7xl px-4 sm:h-20 sm:px-6 lg:px-8">
        <div className="flex h-full items-center justify-between">
          <LinkAnchor
            href="#"
            className="flex items-center gap-2 hover:no-underline"
          >
            {business.logoUrl ? (
              <img
                src={business.logoUrl}
                alt={business.businessName}
                className="h-9 w-9 object-contain"
              />
            ) : (
              <div
                className={cn(
                  "flex h-9 w-9 items-center justify-center bg-accent",
                  CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                )}
              >
                <span className="font-display text-lg font-semibold text-accent-foreground">
                  {business.businessName.charAt(0)}
                </span>
              </div>
            )}
            <span className="font-display text-xl font-semibold text-foreground transition-colors duration-300">
              {business.businessName}
            </span>
          </LinkAnchor>

          <div className="hidden items-center gap-8 lg:flex">
            {sectionLinks.map((link) => (
              <LinkAnchor
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-sm font-medium text-muted-foreground transition-colors duration-300 hover:text-accent hover:no-underline"
              >
                {link.label}
              </LinkAnchor>
            ))}
          </div>

          <div className="hidden items-center gap-4 lg:flex">
            <LinkAnchor
              href={`tel:${business.phone}`}
              className="flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-primary/90 hover:no-underline"
            >
              <Phone className="h-4 w-4 shrink-0 text-primary" />
              {business.phone}
            </LinkAnchor>
            <ButtonLink
              href="#contact"
              onClick={(e) => handleNavClick(e, "#contact")}
              size="sm"
              variant="default"
            >
              {showQuoteForm ? "Get Free Quote" : "Contact Us"}
            </ButtonLink>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
              "p-2 text-foreground lg:hidden",
              CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
            )}
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {isOpen ? (
          <div
            className={cn(
              "mt-2 border border-border bg-card p-4 shadow-xl lg:hidden",
              CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
            )}
          >
            {sectionLinks.map((link) => (
              <LinkAnchor
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={cn(
                  "block px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted hover:text-accent hover:no-underline",
                  CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                )}
              >
                {link.label}
              </LinkAnchor>
            ))}
            <div className="mt-3 space-y-3 border-t border-border pt-3">
              <LinkAnchor
                href={`tel:${business.phone}`}
                className="flex items-center gap-2 px-4 text-sm font-semibold text-primary hover:text-primary/90 hover:no-underline"
              >
                <Phone className="h-4 w-4 shrink-0 text-primary" />
                {business.phone}
              </LinkAnchor>
              <ButtonLink
                href="#contact"
                onClick={(e) => handleNavClick(e, "#contact")}
                variant="default"
                className="w-full"
              >
                {showQuoteForm ? "Get Free Quote" : "Contact Us"}
              </ButtonLink>
            </div>
          </div>
        ) : null}
      </div>
    </nav>
  );
};
